import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Alert,
  Snackbar,
  Alert as MuiAlert,
} from "@mui/material";
import { getAuth } from "firebase/auth";
import useStore from "../../../../store/useStore";
import {
  updateProfileInFirestore,
  // deleteUserProfileFromFirestore,
  updateProfileImageInAllCollections,
  createUserProfileInFirestore,
  fetchAzureUserProfile,
  fetchUserProfileFromFirestore,
  checkUserNameAvailability,
  updateAzureUserProfileUsername,
} from "../../../../services/profileService";
import { useTheme } from "styled-components";
import UserProfile from "../../../../models/UserProfile";
import ProfileImage from "./ProfilePicture/ProfileImage";
import ProfilePictureControls from "./ProfilePicture/ProfilePictureControls";
import UserNameLabel from "../../../atoms/Profile/Edit/UserNameLabel";
import DisplayNameLabel from "../../../atoms/Profile/Edit/DisplayNameLabel";
import BioLabel from "../../../atoms/Profile/Edit/BioLabel";
import UserNameInput from "./UserNameInput";
import DisplayNameInput from "./DisplayNameInput";
import BioInput from "./BioInput";
import ActionButtonsGroup from "./ActionButtons/ActionButtonsGroup";
// import DeleteAccountSection from "../../../molecules/UserProfile/Edit/DeleteAccountSection";

const ProfileForm = () => {
  const { userProfile, setUserProfile } = useStore();
  const navigate = useNavigate();
  const theme = useTheme();
  const [formData, setFormData] = useState({
    userName: "",
    displayName: "",
    bio: "",
    profileImage: "",
  });
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [userNameError, setUsernameError] = useState(false);
  const [userNameDisabled, setUserNameDisabled] = useState(false);
  const [usernameAvailability, setUsernameAvailability] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [user, setUser] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    setUser(user);
    console.log('edit profile user.uid: ', user.uid);


    if (user && !userProfile) {
      const fetchProfile = async () => {
        setLoadingProfile(true);
        try {
          // Try fetching the profile from Firestore
          const firestoreProfile = await fetchUserProfileFromFirestore(
            user.uid
          );

          if (firestoreProfile) {
            setUserProfile(firestoreProfile);
            setFormData({
              userName: firestoreProfile.userName,
              displayName: firestoreProfile.displayName,
              bio: firestoreProfile.bio,
              profileImage: firestoreProfile.profileImage,
            });

            // Check if userName is an email address and set userNameDisabled
            if (firestoreProfile.userName.includes("@")) {
              setUsernameError(true);
              setUserNameDisabled(false); // Allow editing if it's an email
            } else {
              setUsernameError(false);
              setUserNameDisabled(true); // Disable editing if it's not an email
            }
          } else {
            // Fetch the Azure profile if Firestore profile doesn't exist
            const azureProfile = await fetchAzureUserProfile(user.email);

            const newUserProfile = new UserProfile({
              uid: user.uid,
              displayName:
                azureProfile.fullName || user.displayName || "New User",
              email: azureProfile.emailAddress || user.email,
              userName: azureProfile.userName || user.email,
              bio: azureProfile.biography || "",
              profileImage: azureProfile.profilePicture || user.photoURL || "",
            });

            // Save the Azure profile to Firestore
            await createUserProfileInFirestore(
              user.uid,
              newUserProfile.toPlainObject()
            );

            // Set Zustand and form data
            setUserProfile(newUserProfile);
            setFormData({
              userName: newUserProfile.userName,
              displayName: newUserProfile.displayName,
              bio: newUserProfile.bio,
              profileImage: newUserProfile.profileImage,
            });

            // Check if userName is an email address and set userNameDisabled
            if (newUserProfile.userName.includes("@")) {
              setUsernameError(true);
              setUserNameDisabled(false); // Allow editing if it's an email
            } else {
              setUsernameError(false);
              setUserNameDisabled(true); // Disable editing if it's not an email
            }
          }
        } catch (error) {
          console.error("Error fetching profile:", error);
        } finally {
          setLoadingProfile(false);
        }
      };

      fetchProfile();
    } else if (userProfile) {
      // If userProfile exists in Zustand, set it to formData
      setFormData({
        userName: userProfile.userName,
        displayName: userProfile.displayName,
        bio: userProfile.bio,
        profileImage: userProfile.profileImage,
      });

      // Check if userName is an email address and set userNameDisabled
      if (userProfile.userName.includes("@")) {
        setUsernameError(true);
        setUserNameDisabled(false); // Allow editing if it's an email
      } else {
        setUsernameError(false);
        setUserNameDisabled(true); // Disable editing if it's not an email
      }
    }
  }, [userProfile, setUserProfile]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "userName") {
      // Check for username availability after user stops typing for 500ms
      setCheckingUsername(true);
      setTimeout(async () => {
        try {
          const isAvailable = await checkUserNameAvailability(value);
          setUsernameAvailability(isAvailable ? "available" : "taken");
        } catch (error) {
          console.error("Error checking username availability:", error);
          setUsernameAvailability("error");
        } finally {
          setCheckingUsername(false);
        }
      }, 500);
    }
  };

  const handleUpdateProfile = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!userProfile || !user || !user.uid) {
      console.error("User profile or UID is missing.");
      return;
    }

    try {
      if (formData.userName !== userProfile.userName) {
        // Update username in the Azure database if it has changed
        await updateAzureUserProfileUsername(formData.userName, user.email);
      }

      // Update the Firebase profile with the new username
      const updatedProfile = { ...userProfile, ...formData, uid: user.uid };
      await updateProfileInFirestore(user.uid, updatedProfile);
      setUserProfile(updatedProfile);

      // Show success snackbar
      setSnackbarMessage("Profile updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      // Clear username errors and alerts
      setUsernameError(false);
      setUsernameAvailability(null);

      // Disable the username field if it's not an email
      if (!updatedProfile.userName.includes("@")) {
        setUserNameDisabled(true);
      }
    } catch (error) {
      // Show error snackbar
      setSnackbarMessage("Error updating profile.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);

      console.error("Error updating profile:", error);
    }
  };

  // const handleDeleteProfile = async () => {
  //   const auth = getAuth();
  //   const user = auth.currentUser;
  //   if (!userProfile || !user || !user.uid) {
  //     console.error("User profile or UID is missing.");
  //     return; // Exit if userProfile or UID is undefined
  //   }
  //   await deleteUserProfileFromFirestore(user.uid);
  //   setUserProfile(null);
  // };

  const handleImageUpload = async (imageUrl) => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!userProfile || !user || !user.uid) return;

    // Update local state
    setFormData((prev) => ({ ...prev, profileImage: imageUrl }));

    // Update Firestore and Zustand store
    const updatedProfile = {
      ...userProfile,
      profileImage: imageUrl,
      uid: user.uid,
    };
    await updateProfileInFirestore(user.uid, updatedProfile);
    setUserProfile(updatedProfile);

    // Update profile image in all relevant collections
    await updateProfileImageInAllCollections(user.uid, imageUrl);
  };

  const handleRemoveProfilePicture = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!userProfile || !user || !user.uid) {
      console.error("User profile or UID is missing.");
      return; // Exit if userProfile or UID is undefined
    }

    const imageUrl = ""; // Set profile image to empty string to indicate no image

    // Update local state
    setFormData((prev) => ({ ...prev, profileImage: imageUrl }));

    // Update Firestore and Zustand store
    const updatedProfile = {
      ...userProfile,
      profileImage: imageUrl,
      uid: user.uid,
    };
    await updateProfileInFirestore(user.uid, updatedProfile);
    setUserProfile(updatedProfile);

    // Update profile image in all relevant collections
    await updateProfileImageInAllCollections(user.uid, imageUrl);
  };

  const handleCancelEdit = () => {
    navigate(`/challenge/profile/${user?.uid}`);
  }

  if (loadingProfile) {
    return (
      <Box>
        <Typography variant="h6" sx={{ textAlign: "center", mt: 4 }}>
          Loading profile...
        </Typography>
      </Box>
    );
  }

  if (!userProfile) {
    // Handle the case where the user profile is being created or is missing
    return (
      <Box>
        <Typography variant="h6" sx={{ textAlign: "center", mt: 4 }}>
          Loading profile...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          fontFamily: "basic-sans",
          fontWeight: 700,
          fontSize: "2em",
          mb: 10,
        }}
      >
        Edit My Profile & Settings
      </Typography>

      {/* Profile Picture Section */}
      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ backgroundColor: "#20112D", p: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "basic-sans",
              fontSize: "1em",
              fontWeight: "bold",
              color: theme.colors.amber,
            }}
          >
            Profile Picture
          </Typography>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        p={2}
        alignItems="center"
        sx={{ backgroundColor: "#2A1E38", mb: 8 }}
      >
        <Grid item xs={12} sm={3} sx={{ textAlign: "center" }}>
          <ProfileImage src={formData.profileImage} />
        </Grid>
        <Grid item xs={12} sm={9}>
          <ProfilePictureControls
            handleRemoveProfilePicture={handleRemoveProfilePicture}
            theme={theme}
            onImageUpload={handleImageUpload}
            userId={userProfile.uid}
          />
        </Grid>
      </Grid>

      {/* Section 2: User Name and Bio Label */}
      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ backgroundColor: "#20112D", p: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "basic-sans",
              fontSize: "1em",
              fontWeight: "bold",
              color: theme.colors.amber,
            }}
          >
            Name and Bio
          </Typography>
        </Grid>
      </Grid>

      {/* Username and Bio Section */}
      <Grid
        container
        spacing={2}
        p={2}
        alignItems="center"
        sx={{ backgroundColor: "#2A1E38" }}
      >
        <UserNameLabel />
        <Grid item xs={12} sm={9}>
          {/* Show error or success based on username availability */}
          {/* Red warning if userName is an email address */}
          {userNameError && (
            <Alert variant="filled" severity="error" sx={{ mb: 4 }}>
              Please create a unique User Name (not an email address).
            </Alert>
          )}

          {checkingUsername && (
            <Alert variant="filled" severity="info" sx={{ mb: 2 }}>
              Checking username availability...
            </Alert>
          )}
          {usernameAvailability === "available" && (
            <Alert
              variant="filled"
              severity="success"
              sx={{ mb: 2, backgroundColor: theme.colors.green }}
            >
              Username is available!
            </Alert>
          )}
          {usernameAvailability === "taken" && (
            <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
              Username is already taken.
            </Alert>
          )}
          {usernameAvailability === "error" && (
            <Alert variant="filled" severity="warning" sx={{ mb: 2 }}>
              Error checking username. Please try again later.
            </Alert>
          )}

          <UserNameInput
            theme={theme}
            userName={formData.userName}
            disabled={userNameDisabled}
            handleChange={handleChange}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        p={2}
        alignItems="center"
        sx={{ backgroundColor: "#2A1E38" }}
      >
        <DisplayNameLabel />
        <Grid item xs={12} sm={9}>
          <DisplayNameInput
            displayName={formData.displayName}
            handleChange={handleChange}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        p={2}
        alignItems="center"
        sx={{ backgroundColor: "#2A1E38", mb: 8 }}
      >
        <BioLabel />
        <Grid item xs={12} sm={9}>
          <BioInput bio={formData.bio} handleChange={handleChange} />
        </Grid>
      </Grid>

      {/* Delete Your Account Section */}
      {/* <Grid container spacing={2}>
        <Grid item xs={12} sx={{ backgroundColor: "#20112D", p: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "basic-sans",
              fontSize: "1em",
              fontWeight: "bold",
              color: theme.colors.amber,
            }}
          >
            Delete Your Account
          </Typography>
        </Grid>
      </Grid>
      <DeleteAccountSection handleDelete={handleDeleteProfile} /> */}

      {/* Action Buttons Section */}
      <ActionButtonsGroup
        handleCancel={handleCancelEdit}
        handleSave={handleUpdateProfile}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert
          onClose={handleSnackbarClose}
          variant="filled"
          severity={snackbarSeverity}
          sx={{ width: "100%", backgroundColor: theme.colors.green }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default ProfileForm;
